import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import FluidButton from 'ui/FluidButton';
import TopNavigationBar from 'pureUi/TopNavigationBar';
import { useDispatch, useSelector } from 'react-redux';
import * as DashboardActions from 'store/modules/bookingManager/subdomains/dashboard/actions';
import * as DashboardSelectors from 'store/modules/bookingManager/subdomains/dashboard/selectors';
import { EUserType } from 'services/BackendApi';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { makeBookingManagerApi } from 'services/BookingManagerApi';
import * as BookingManagerSelectors from 'store/modules/bookingManager/selectors';
import { useVouchersV2BookingManagerQueries } from './useVouchersV2BookingManagerQueries';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';
import { useTabFocus } from 'hooks/useTabFocus';
import { formatDateTimeDisplay } from 'utils/date';
import CopyIcon from 'ui/Icons/copy.component.svg';
import { useNotifications } from 'hooks/useNotifications';
import { VerticalSpace } from 'ui/VerticalSpace';
import { TextInput } from 'ui/TextInput';
import { Table } from 'ui/Table';
import { ESortOrder } from 'store/common/types';
import { ITableColumn } from 'ui/Table/types';
import { useVouchersV2AccessListQueries } from './useVouchersV2AccessQueries';
import { isValidEmail } from 'utils/email';
import { CircleIconButton } from 'ui/CircleIconButton';
import { format } from 'date-fns';
import XIcon from 'ui/Icons/tabler/x.component.svg';
import { useVoucherPdfExport } from '../Voucher/useVoucherPdfExport';
import { getAccessUuidForItem } from 'containers/Voucher/utils';

const tableColumns: ITableColumn[] = [
  {
    id: 'name',
    label: 'Name',
    width: '350px',
    fieldToSortBy: 'name',
  },
  {
    id: 'email',
    label: 'Email',
    width: '350px',
    fieldToSortBy: 'email',
  },
  {
    id: 'shared',
    label: 'Shared',
    width: '75px',
    fieldToSortBy: 'shared',
  },
  {
    id: 'sharedBy',
    label: 'Shared By',
    width: '120px',
    fieldToSortBy: 'sharedBy',
  },
  {
    id: 'cta',
    label: '',
    width: '34px',
  },
];

export const BookingManagerVouchersV2 = () => {
  const { bookingUuid } = useParams<{ bookingUuid: string }>();
  const [fields, setFields] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [fieldErrors, setFieldErrors] = React.useState<{ [key: string]: string | null }>({});
  const [tableRows, setTableRows] = React.useState([]);
  const [sortBy, setSortBy] = React.useState('firstName');
  const [sortOrder, setSortOrder] = React.useState<ESortOrder>(ESortOrder.ASC);

  const userRole = useSelector(AuthSelectors.getCurrentUserType);
  const isTa = useSelector(AuthSelectors.isTA);
  const topNavigationData = useSelector(DashboardSelectors.topNavigationDataDashboardSelector);
  const bookingInformationReservationTeamData = useSelector(
    DashboardSelectors.bookingInformationReservationTeamDashboardSelector
  );
  const booking = useSelector(BookingManagerSelectors.bookingSelector);

  const notificationOps = useNotifications();
  const dispatch = useDispatch();

  const bookingManagerApi = makeBookingManagerApi();
  const itineraryManagerApi = makeItineraryManagerApi();

  const vouchersV2Queries = useVouchersV2BookingManagerQueries(
    bookingManagerApi,
    itineraryManagerApi,
    booking?.bookingUuid
  );
  const vouchersV2AccessList = useVouchersV2AccessListQueries(itineraryManagerApi, booking?.bookingUuid);

  const storedAccessUuid = getAccessUuidForItem(booking?.bookingUuid!);

  const { generatePdfAsLoggedIn } = useVoucherPdfExport(itineraryManagerApi, booking?.bookingUuid, storedAccessUuid);

  const isNetworkPending =
    vouchersV2Queries.getVouchersV2Overview.isFetching || vouchersV2Queries.postGenerateVouchersV2Instance.isPending;
  const vouchersV2Overview = vouchersV2Queries.getVouchersV2Overview.data?.data;
  const isAccessListPending =
    vouchersV2AccessList.getVoucherV2AccessList.isFetching || vouchersV2AccessList.addToVoucherV2AccessList.isPending;

  const handleFieldChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = e.target;
      setFields(prev => ({ ...prev, [id]: value }));
      setFieldErrors(prev => ({ ...prev, [id]: null }));
    },
    [setFields, setFieldErrors]
  );

  const validateFields = React.useCallback(() => {
    const errs: { [key: string]: string | null } = {};
    Object.entries(fields).forEach(([key, value]) => {
      if (!value?.trim()) {
        errs[key] = 'Field required';
      } else if (key === 'email' && !isValidEmail(value.trim())) {
        errs[key] = 'Must be valid email';
      } else {
        errs[key] = null;
      }
    });
    setFieldErrors(errs);
    return Object.values(errs).every(error => !error);
  }, [fieldErrors, setFieldErrors]);

  const handleShareExternalUser = React.useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      if (!validateFields()) {
        return;
      }
      const { firstName, lastName, email } = fields;
      if (!booking || !firstName || !lastName || !email) {
        return;
      }
      await vouchersV2AccessList.addToVoucherV2AccessList.mutateAsync({
        voucherUuid: booking.bookingUuid,
        firstName,
        lastName,
        email,
      });
      setFields({ firstName: '', lastName: '', email: '' });
    },
    [booking, fields, vouchersV2AccessList]
  );

  useTabFocus(() => {
    vouchersV2Queries.invalidateGetVouchersV2Instance();
  }, 3000);

  useEffect(() => {
    dispatch(DashboardActions.getTopNavigationDataRequestAction(bookingUuid));
  }, [bookingUuid]);

  useEffect(() => {
    if (!vouchersV2AccessList.getVoucherV2AccessList.data?.data) {
      return;
    }

    const accessList = vouchersV2AccessList.getVoucherV2AccessList.data.data;

    const rows = accessList.map(access => ({
      id: access.uuid,
      cells: [
        { id: 'name', value: `${access.firstName} ${access.lastName}` },
        { id: 'email', value: access.email },
        { id: 'shared', value: format(new Date(access.createdAt), 'd MMM yy') },
        { id: 'sharedBy', value: access.createdBy },
        {
          id: 'cta',
          value: (
            <CircleIconButton
              type="secondary"
              IconComponent={XIcon}
              iconSize="sm"
              onClick={() => {
                if (!booking) {
                  return;
                }
                vouchersV2AccessList.removeFromVoucherV2AccessList.mutateAsync(access.email);
              }}
            />
          ),
        },
      ],
    }));
    setTableRows(rows);
  }, [vouchersV2AccessList.getVoucherV2AccessList.data]);

  return (
    <>
      <TopNavigationBar
        userRole={userRole as EUserType}
        data={topNavigationData}
        bookingInformationReservationTeamData={bookingInformationReservationTeamData}
        isError={false}
      />

      <h1 className="text-black text-[36px] font-noe-display font-normal m-0">Voucher</h1>

      {isNetworkPending ? (
        <>
          {vouchersV2Queries.getVouchersV2Overview.isFetching && <LoadingBar label="Loading voucher data..." />}
          {vouchersV2Queries.postGenerateVouchersV2Instance.isPending && <LoadingBar label="Generating voucher..." />}
        </>
      ) : null}

      {!isNetworkPending && (
        <>
          {vouchersV2Overview && (
            <>
              <div className="grid grid-cols-2 font-pt-sans text-black mt-20px">
                <div className="flex flex-col gap-5px">
                  <span className="text-15px">
                    <span className="font-bold">Created by: </span>
                    {vouchersV2Overview.createdBy}, {formatDateTimeDisplay(vouchersV2Overview.createdAt)}
                  </span>
                  {vouchersV2Overview.updatedBy && (
                    <span className="text-15px">
                      <span className="font-bold">Modified by: </span>
                      {vouchersV2Overview.updatedBy}, {formatDateTimeDisplay(vouchersV2Overview.updatedAt)}
                    </span>
                  )}
                  <span className="text-15px">
                    <span className="font-bold">Shareable link: </span>
                    <a
                      className="!text-brown-prime !underline"
                      href={`/voucher/${booking?.bookingUuid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {window.location.origin}/voucher/{booking?.bookingUuid}
                    </a>
                    <div className="inline-block p-0 translate-x-10px translate-y-4px">
                      <button
                        className="text-brown-prime h-26px cursor-pointer p-0 bg-brown-10 active:bg-gray-20 rounded outline-none"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(
                              `${window.location.origin}/voucher/${booking?.bookingUuid}`
                            );
                            notificationOps.showSuccessNotification('Shareable link copied to clipboard');
                          } catch (e) {
                            notificationOps.showErrorNotification('Failed to copy shareable link to clipboard');
                          }
                        }}
                      >
                        <CopyIcon className="w-[20px] fill-brown-prime" />
                      </button>
                    </div>
                  </span>
                </div>
                <div className="justify-self-end self-start flex items-center gap-2">
                  <FluidButton
                    onClick={() => generatePdfAsLoggedIn.mutate()}
                    type="secondary"
                    isLoading={generatePdfAsLoggedIn.isPending}
                  >
                    Export PDF
                  </FluidButton>
                  <FluidButton
                    onClick={() => {
                      window.open(`/voucher/${booking?.bookingUuid}`, '_blank', 'noopener,noreferrer');
                    }}
                    type="primary"
                  >
                    View
                  </FluidButton>
                </div>
              </div>
              <VerticalSpace height="40px" />
              <div className="font-pt-sans text-black">
                <div className="font-bold text-[17px]">External Authorized Viewers</div>
                <div className="text-[16px]">Share with external users who need access to this document.</div>
                <VerticalSpace height="20px" />
                <div className="flex gap-5 items-start h-[77px]">
                  <TextInput
                    id="firstName"
                    placeholder="Enter first name"
                    value={fields.firstName}
                    onChange={handleFieldChange}
                    type="text"
                    className="w-[250px]"
                    label="First Name *"
                    required
                    disabled={isAccessListPending}
                    errorMessage={fieldErrors.firstName}
                  />
                  <TextInput
                    id="lastName"
                    placeholder="Enter last name"
                    value={fields.lastName}
                    onChange={handleFieldChange}
                    type="text"
                    className="w-[250px]"
                    label="Last Name *"
                    required
                    disabled={isAccessListPending}
                    errorMessage={fieldErrors.lastName}
                  />
                  <TextInput
                    id="email"
                    placeholder="Enter email address"
                    value={fields.email}
                    onChange={handleFieldChange}
                    type="email"
                    className="w-full"
                    label="Email Address *"
                    required
                    disabled={isAccessListPending}
                    errorMessage={fieldErrors.email}
                  />
                  <FluidButton
                    fixedWidth="67px"
                    type="secondary"
                    className="min-w-[67px] mt-[22px]"
                    isLoading={vouchersV2AccessList.addToVoucherV2AccessList.isPending}
                    onClick={handleShareExternalUser}
                  >
                    Share
                  </FluidButton>
                </div>
                <VerticalSpace height="20px" />
                <Table
                  columns={tableColumns}
                  rows={tableRows}
                  messageWhenEmpty="No viewers added yet"
                  loading={isAccessListPending}
                  onSort={(newSortBy, newSortOrder) => {
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder as ESortOrder);
                  }}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                />
              </div>
            </>
          )}

          {!vouchersV2Overview && !isTa && (
            <>
              <p className="m-0 my-20px italic text-black font-pt-sans">There is no voucher created yet</p>
              <FluidButton
                onClick={async () => {
                  if (!booking) {
                    return;
                  }
                  vouchersV2Queries.postGenerateVouchersV2Instance.mutate(booking.bookingUuid);
                }}
                className="w-[145px]"
                type="primary"
              >
                Create Voucher
              </FluidButton>
            </>
          )}
        </>
      )}
    </>
  );
};
